// 공용 입력 필드.
// 라벨/에러/도움말을 aria 로 올바르게 연결해 접근성을 기본 보장한다(중장년/스크린리더 대응).
// 라벨은 시각적으로 노출하되, 필요 시 visuallyHidden 로 숨기고 aria 로만 연결할 수 있다.

import { useId } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { cn } from "./cn.ts";

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: ReactNode;
  hideLabel?: boolean;
  error?: string;
  hint?: string;
}

// 라벨/도움말/에러를 aria 로 자동 연결하는 공용 입력. 접근성을 기본 보장한다.
export default function Input({
  label,
  hideLabel = false,
  error,
  hint,
  className,
  required,
  ...rest
}: InputProps) {
  // 라벨/도움말/에러 연결용 안정적 id 생성
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className={cn(
          "text-sm font-medium text-ink-700",
          hideLabel && "sr-only",
        )}
      >
        {label}
        {/* 필수 표시: 색만이 아니라 텍스트로도 의미 전달 */}
        {required ? <span className="text-danger-600"> (필수)</span> : null}
      </label>
      <input
        id={id}
        required={required}
        // 에러가 있으면 보조기기에 알리고 도움말/에러를 설명으로 연결
        aria-invalid={error ? true : undefined}
        aria-describedby={cn(hintId, errorId) || undefined}
        className={cn(
          "min-h-[3rem] rounded border bg-surface px-4 text-base text-ink-900",
          "placeholder:text-ink-500",
          "focus-visible:border-brand-600",
          error ? "border-danger-600" : "border-ink-300",
          className,
        )}
        {...rest}
      />
      {hint && !error ? (
        <p id={hintId} className="text-sm text-ink-600">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-sm font-medium text-danger-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
